import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import DataTable from "components/DataTable/DataTable.jsx";

import api from "api/index";

const BLANK_FORM = { name: "" };

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "220px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #0d1b6e 0%, #1a237e 60%, #1565c0 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    color: "#fff"
  },
  heroTitle: { fontSize: "32px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },

  kpiRow: { display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: "200px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s ease",
    border: "1px solid #f1f5f9",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 30px 0 rgba(0,0,0,0.08)" }
  },
  iconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "20px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "12px", color: "#64748b", margin: "2px 0 0", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" },
  toolbarWrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 25px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    gap: "20px",
    flexWrap: "wrap"
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    background: "#fff",
    padding: "8px 16px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    width: "350px",
    transition: "all 0.2s ease",
    "&focus-within": {
      borderColor: "#1a237e",
      boxShadow: "0 0 0 4px rgba(26, 35, 126, 0.05)"
    }
  },
  searchInput: {
    border: "none",
    outline: "none",
    marginLeft: "10px",
    fontSize: "14px",
    width: "100%",
    color: "#1e293b",
    "&::placeholder": { color: "#94a3b8" }
  },

  errorMsg: { color: "#f44336", fontSize: "13px" },
  formControl: { width: "100%", marginTop: "16px" },
  inlineAdd: {
    display: "flex", gap: "12px", alignItems: "center",
    background: "rgba(26, 35, 126, 0.03)", borderRadius: "16px", padding: "18px 24px", marginBottom: "28px",
    border: "1px solid rgba(26, 35, 126, 0.08)",
    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)"
  },
  departmentName: { fontWeight: "700", color: "#1a237e", cursor: "pointer", textDecoration: "none" },
  countBadge: {
    background: "#e8eaf6", color: "#1a237e", padding: "3px 10px", borderRadius: "12px",
    fontSize: "11px", fontWeight: "700"
  },
  tableRow: {
    transition: "all 0.2s ease",
    "&:hover": { background: "rgba(26, 35, 126, 0.02)" }
  }
};

class DepartmentList extends Component {
  state = {
    departments: [],
    loading: true,
    open: false,
    editing: null,
    form: { ...BLANK_FORM },
    saving: false,
    error: null,
    formError: null,
    confirmDeleteId: null,
    searchQuery: ""
  };

  componentDidMount() {
    this.fetchDepartments();
  }

  fetchDepartments = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await api.get("/departments");
      const d = res.data.data || res.data;
      this.setState({ departments: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) {
      this.setState({ departments: [], loading: false, error: "Could not load departments." });
    }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  openEdit = dept => {
    this.setState({
      open: true,
      editing: dept.id,
      formError: null,
      form: { name: dept.name || "" }
    });
  };

  openAdd = () => {
    this.setState({
      open: true,
      editing: null,
      formError: null,
      form: { ...BLANK_FORM }
    });
  };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.name.trim()) {
      this.setState({ formError: "Department name is required." });
      return;
    }
    this.setState({ saving: true, formError: null });
    try {
      if (editing) {
        await api.put(`/departments/${editing}`, { name: form.name.trim() });
      } else {
        await api.post("/departments", { name: form.name.trim() });
      }
      this.setState({ open: false, saving: false });
      this.fetchDepartments();
    } catch (e) {
      this.setState({ formError: e.response?.data?.message || `Failed to ${editing ? "update" : "add"} department.`, saving: false });
    }
  };

  handleDelete = async id => {
    this.setState({ confirmDeleteId: null });
    try {
      await api.delete(`/departments/${id}`);
      this.fetchDepartments();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to delete department. It may have associated programmes or staff." });
    }
  };

  render() {
    const { classes, history } = this.props;
    const {
      departments, loading, searchQuery,
      open, editing, form, saving, error, formError, confirmDeleteId
    } = this.state;

    const filteredDepartments = departments.filter(d =>
      d.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const totalDepts = departments.length;
    const totalProgs = departments.reduce((acc, d) => acc + (d.programmes || 0), 0);
    const totalStaff = departments.reduce((acc, d) => acc + (d.staff || 0), 0);

    const columns = [
      {
        id: "name",
        header: "Department Name",
        cell: info => {
          const dept = info.row.original;
          return (
            <a
              className={classes.departmentName}
              onClick={() => history.push(`/bwillis/academic/departments/${dept.id}`)}
            >
              {dept.name || "—"}
            </a>
          );
        }
      },
      {
        id: "programmes",
        header: "Programmes",
        cell: info => {
          const dept = info.row.original;
          return <span className={classes.countBadge}>{dept.programmes || 0} programme{dept.programmes !== 1 ? "s" : ""}</span>;
        }
      },
      {
        id: "staff",
        header: "Staff",
        cell: info => {
          const dept = info.row.original;
          return (
            <span className={classes.countBadge} style={{ background: "#e8f5e9", color: "#2e7d32" }}>
              {dept.staff || 0} staff
            </span>
          );
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => {
          const dept = info.row.original;
          return (
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <Button size="sm" color="info" simple onClick={() => this.openEdit(dept)} style={{ padding: "6px" }}>
                <Icon style={{ fontSize: "20px" }}>edit</Icon>
              </Button>
              <Button size="sm" color="danger" simple onClick={() => this.setState({ confirmDeleteId: dept.id })} style={{ padding: "6px" }}>
                <Icon style={{ fontSize: "20px" }}>delete</Icon>
              </Button>
            </div>
          );
        }
      }
    ];

    const emptyState = (
      <div style={{ textAlign: "center", padding: "80px 40px" }}>
        <Icon style={{ fontSize: "64px", color: "#e2e8f0", marginBottom: "16px" }}>business</Icon>
        <p style={{ color: "#64748b", fontSize: "16px", margin: 0 }}>
          {searchQuery ? "No departments match your search." : "No departments found. Click 'New Department' to start."}
        </p>
      </div>
    );

    return (
      <GridContainer>
        <GridItem xs={12}>
          <div className={classes.heroWrap}>
            <div className={classes.heroOverlay} />
            <div className={classes.heroContent}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                <div>
                  <h1 className={classes.heroTitle}>Academic Departments</h1>
                  <p className={classes.heroSub}>
                    Manage institutional departments and the programmes and staff attached to each.
                  </p>
                </div>
                <Button
                  color="white"
                  round
                  onClick={this.openAdd}
                  style={{
                    fontWeight: "800",
                    padding: "12px 24px",
                    boxShadow: "0 4px 14px 0 rgba(0,0,0,0.2)"
                  }}
                >
                  <Icon style={{ marginRight: "8px" }}>add_business</Icon> New Department
                </Button>
              </div>
            </div>
          </div>
        </GridItem>

        <GridItem xs={12}>
          <div className={classes.kpiRow}>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e3f2fd", color: "#1565c0" }}>
                <Icon>business</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{totalDepts}</p>
                <p className={classes.statLabel}>Total Departments</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e8eaf6", color: "#3949ab" }}>
                <Icon>school</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{totalProgs}</p>
                <p className={classes.statLabel}>Total Programmes</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "#e8f5e9", color: "#2e7d32" }}>
                <Icon>groups</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{totalStaff}</p>
                <p className={classes.statLabel}>Total Staff</p>
              </div>
            </div>
          </div>
        </GridItem>

        <GridItem xs={12}>
          <div className={classes.toolbarWrap} style={{ borderRadius: "16px 16px 0 0" }}>
            <div className={classes.searchBox}>
              <Icon style={{ color: "#94a3b8", fontSize: "20px" }}>search</Icon>
              <input
                type="text"
                className={classes.searchInput}
                placeholder="Search departments..."
                value={searchQuery}
                onChange={e => this.setState({ searchQuery: e.target.value })}
              />
            </div>
            <div style={{ color: "#64748b", fontSize: "14px", fontWeight: 500 }}>
              Showing <b>{filteredDepartments.length}</b> departments
            </div>
          </div>
          {error && <p className={classes.errorMsg} style={{ padding: "10px 0" }}>{error}</p>}
          <DataTable
            columns={columns}
            data={filteredDepartments}
            isLoading={loading}
            emptyState={emptyState}
          />
        </GridItem>

        {/* Add/Edit Dialog */}
        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="xs" fullWidth>
          <DialogHeader
            title={editing ? "Edit Department" : "New Department"}
            subtitle={editing ? "Update this department's details." : "Add a new department to the institution."}
            icon={editing ? "edit" : "add_business"}
            color={editing ? "primary" : "info"}
            onClose={() => this.setState({ open: false })}
          />
          <DialogContent style={{ paddingTop: "20px" }}>
            {formError && <p className={classes.errorMsg} style={{ marginBottom: "15px" }}>{formError}</p>}
            <CustomInput
              labelText="Department Name"
              id="deptEditName"
              formControlProps={{ fullWidth: true, style: { margin: 0 } }}
              inputProps={{
                value: form.name,
                onChange: this.handleChange("name"),
                placeholder: "e.g. Science and Technology",
                onKeyDown: e => e.key === "Enter" && this.handleSave()
              }}
            />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving} style={{ fontWeight: "700" }}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : (editing ? "Update Details" : "Create Department")}
            </Button>
          </DialogFooter>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!confirmDeleteId} onClose={() => this.setState({ confirmDeleteId: null })} maxWidth="xs" fullWidth>
          <DialogHeader
            title="Confirm Deletion"
            icon="delete_outline"
            color="danger"
            onClose={() => this.setState({ confirmDeleteId: null })}
          />
          <DialogContent style={{ paddingTop: "20px", textAlign: "center" }}>
            <div style={{
              width: "60px", height: "60px", borderRadius: "50%", background: "#ffebee",
              display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px"
            }}>
              <Icon style={{ color: "#d32f2f", fontSize: "32px" }}>warning</Icon>
            </div>
            <h5 style={{ fontWeight: "700", marginBottom: "10px" }}>Are you sure?</h5>
            <p style={{ color: "#666" }}>
              This will permanently delete the department. Departments with attached programmes or staff cannot be deleted until those are detached.
            </p>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ confirmDeleteId: null })}>Cancel</Button>
            <Button color="danger" onClick={() => this.handleDelete(confirmDeleteId)} style={{ fontWeight: "700" }}>
              Confirm Delete
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

DepartmentList.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(DepartmentList));
